import { Fragment } from 'react'
import { Trans } from 'react-i18next'
import { format, isValid, parseISO } from 'date-fns'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { DATE_FULL_FORMAT_FROM_TIME } from '../../../constants/date'
import { REQUEST_STATUS } from '../../../constants/request'
import { PROFILE_ROUTE_NAMES } from '../../../constants/route'
import { isValueNumberType } from '../../../utils/common'
import { toUkTimezoneDate } from '../../../utils/date'
import { htmlToMultilineText } from '../../../utils/html'
import { getUserProfileRoute } from '../../../utils/user'
import {
  ENTITY_PROFILE_TAB_UPDATES_CONTENT_TRANS_PROPS,
  EntityProfileTabUpdatesCardEntityLink,
  getEntityProfileTabUpdateOwner,
} from '../../Entity'
import { getRequestTitle } from '../../Request'

import {
  REQUEST_LOG_EVENT_ACTION,
  REQUEST_LOG_EVENT_ACTION_REMOVED_TRANSLATION_KEY,
  REQUEST_LOG_EVENT_ACTION_SET_TRANSLATION_KEY,
  REQUEST_LOG_EVENT_ACTION_TRANSLATION_KEY,
  REQUEST_LOG_EVENT_ACTION_WITH_COMMENT_TRANSLATION_KEY,
  REQUEST_LOG_EVENT_DOC_FLOW_ACTIONS,
  REQUEST_LOG_EVENT_EMPTY_VALUE,
  REQUEST_LOG_EVENT_ENTITY_LINK_CONFIG,
  REQUEST_LOG_EVENT_ITEMS_NAME_KEY,
  REQUEST_LOG_EVENT_RELATED_REQUEST_ACTIONS,
  REQUEST_LOG_EVENT_RELATED_VALUE_KEY,
  REQUEST_LOG_EVENT_TRANSLATION_TAG_REGEXP,
  REQUEST_LOG_EVENT_USER_ACTION_ROLE,
} from './RequestProfileTabUpdates.constants'

function isRequestLogEventEmptyValue(value) {
  return value == null || value === EMPTY_STRING
}

function isRequestLogEventUpdateTextAction(action) {
  return action === REQUEST_LOG_EVENT_ACTION.UPDATE_TEXT
}

function isRequestLogEventUpdateStatusAction(action) {
  return action === REQUEST_LOG_EVENT_ACTION.UPDATE_STATUS
}

function isRequestLogEventUpdateDueDateAction(action) {
  return action === REQUEST_LOG_EVENT_ACTION.UPDATE_DUE_DATE
}

function isRequestLogEventEntityLinkAction(action) {
  return Boolean(REQUEST_LOG_EVENT_ENTITY_LINK_CONFIG[action])
}

function isRequestLogEventUsersAction(action) {
  return Boolean(REQUEST_LOG_EVENT_USER_ACTION_ROLE[action])
}

function isRequestLogEventRelatedRequestsAction(action) {
  return REQUEST_LOG_EVENT_RELATED_REQUEST_ACTIONS.includes(action)
}

function isRequestLogEventDocFlowsAction(action) {
  return REQUEST_LOG_EVENT_DOC_FLOW_ACTIONS.includes(action)
}

function getRequestLogEventValueLabel(value) {
  return isRequestLogEventEmptyValue(value)
    ? REQUEST_LOG_EVENT_EMPTY_VALUE
    : value
}

// The description is TinyMCE HTML — normalize it to plain text once per item, so
// markup-only values ("<p></p>") count as empty and tags never leak into the log.
function getRequestLogEventNormalizedItem(item) {
  if (!isRequestLogEventUpdateTextAction(item?.action)) {
    return item
  }

  const data = item.data || EMPTY_OBJECT

  return {
    ...item,
    data: {
      ...data,
      new_value: htmlToMultilineText(data.new_value),
      old_value: htmlToMultilineText(data.old_value),
    },
  }
}

function getRequestLogEventStatusLabel(value, t) {
  const status = isValueNumberType(value) ? REQUEST_STATUS[value] : null

  if (status) {
    return t(status.text)
  }

  return value ?? REQUEST_LOG_EVENT_EMPTY_VALUE
}

function formatRequestLogEventDueDate({ timeZone, value }) {
  if (!value) {
    return REQUEST_LOG_EVENT_EMPTY_VALUE
  }

  const parsed = parseISO(value)

  if (!isValid(parsed)) {
    return value
  }

  return format(toUkTimezoneDate(parsed, timeZone), DATE_FULL_FORMAT_FROM_TIME)
}

function getRequestLogEventRelatedRequestName(request) {
  return getRequestTitle({
    requestNumber: request?.request_no,
    title: request?.title,
  })
}

function getRequestLogEventEntityLinkRoute({ config, linkContext, value }) {
  return value?.[config.idKey] == null
    ? undefined
    : config.getRoute(value[config.idKey], linkContext)
}

function getRequestLogEventEntityLinkContent({
  item,
  linkContext,
  translationKey,
  values,
}) {
  const config = REQUEST_LOG_EVENT_ENTITY_LINK_CONFIG[item.action]
  const data = item.data || EMPTY_OBJECT

  const prevRoute = getRequestLogEventEntityLinkRoute({
    config,
    linkContext,
    value: data.old_value,
  })
  const currRoute = getRequestLogEventEntityLinkRoute({
    config,
    linkContext,
    value: data.new_value,
  })

  if (!prevRoute && !currRoute) {
    return null
  }

  return (
    <Trans
      {...ENTITY_PROFILE_TAB_UPDATES_CONTENT_TRANS_PROPS}
      components={{
        currLink: <EntityProfileTabUpdatesCardEntityLink to={currRoute} />,
        prevLink: <EntityProfileTabUpdatesCardEntityLink to={prevRoute} />,
      }}
      i18nKey={translationKey}
      values={values}
    />
  )
}

// Translated sentence (ends with a colon) followed by a comma-separated list of
// entries, each wrapped in a profile link when its route is resolvable.
function getRequestLogEventListContent({
  entries,
  getEntryLabel,
  getEntryRoute,
  t,
  translationKey,
  values,
}) {
  if (entries.length === 0) {
    return null
  }

  return (
    <>
      {t(translationKey, values)}{' '}
      {entries.map((entry, index) => (
        <Fragment key={entry.id ?? entry.owner_id ?? index}>
          {index > 0 && ', '}
          <EntityProfileTabUpdatesCardEntityLink to={getEntryRoute(entry)}>
            {getEntryLabel(entry)}
          </EntityProfileTabUpdatesCardEntityLink>
        </Fragment>
      ))}
    </>
  )
}

function getRequestLogEventRelatedRequestsContent({
  item,
  t,
  translationKey,
  values,
}) {
  return getRequestLogEventListContent({
    entries: item.data?.items || EMPTY_LIST,
    getEntryLabel: getRequestLogEventRelatedRequestName,
    getEntryRoute: request =>
      request.id == null
        ? undefined
        : PROFILE_ROUTE_NAMES.requestProfileNG({ id: request.id }),
    t,
    translationKey,
    values,
  })
}

function getRequestLogEventDocFlowsContent({
  item,
  t,
  translationKey,
  values,
}) {
  return getRequestLogEventListContent({
    entries: item.data?.items || EMPTY_LIST,
    getEntryLabel: docFlow => docFlow.name,
    getEntryRoute: docFlow => PROFILE_ROUTE_NAMES.docFlow(docFlow.id),
    t,
    translationKey,
    values,
  })
}

function getRequestLogEventUsersContent({
  item,
  linkContext,
  t,
  translationKey,
  values,
}) {
  const nameKey = REQUEST_LOG_EVENT_ITEMS_NAME_KEY[item.action]
  const role = REQUEST_LOG_EVENT_USER_ACTION_ROLE[item.action]

  return getRequestLogEventListContent({
    entries: (item.data?.items || EMPTY_LIST).filter(entry => entry?.[nameKey]),
    getEntryLabel: entry => entry[nameKey],
    getEntryRoute: entry =>
      entry.owner_id == null
        ? undefined
        : getUserProfileRoute({ ...linkContext, id: entry.owner_id, role }),
    t,
    translationKey,
    values,
  })
}

/**
 * Rich `content` (link-bearing node) for building/flat/applicant changes,
 * user-list events (assignees/providers/side workers) and related-request and
 * doc flow attach/detach. Returns `null` for other actions (the card renders
 * the plain `text`) and on connected requests — an id2id payload carries the
 * partner company's ids, so local routes would be wrong.
 */
function getRequestLogEventItemContent({
  item,
  linkContext,
  outboundUuid,
  t,
  translationKey,
  values,
}) {
  if (outboundUuid) {
    return null
  }

  if (isRequestLogEventEntityLinkAction(item?.action)) {
    return getRequestLogEventEntityLinkContent({
      item,
      linkContext,
      translationKey,
      values,
    })
  }

  if (isRequestLogEventUsersAction(item?.action)) {
    return getRequestLogEventUsersContent({
      item,
      linkContext,
      t,
      translationKey,
      values,
    })
  }

  if (isRequestLogEventRelatedRequestsAction(item?.action)) {
    return getRequestLogEventRelatedRequestsContent({
      item,
      t,
      translationKey,
      values,
    })
  }

  if (isRequestLogEventDocFlowsAction(item?.action)) {
    return getRequestLogEventDocFlowsContent({
      item,
      t,
      translationKey,
      values,
    })
  }

  return null
}

function getRequestLogEventItemText({ item, t, translationKey, values }) {
  const text = t(translationKey, values)

  // The list keys (related requests, doc flows, users) end with a colon; the
  // list is appended so the rich `content` variant can render each entry as a
  // separate link.
  if (isRequestLogEventRelatedRequestsAction(item.action)) {
    return values.requests ? `${text} ${values.requests}` : text
  }

  if (isRequestLogEventDocFlowsAction(item.action)) {
    return values.docFlows ? `${text} ${values.docFlows}` : text
  }

  if (isRequestLogEventUsersAction(item.action)) {
    return values.names ? `${text} ${values.names}` : text
  }

  if (isRequestLogEventEntityLinkAction(item.action)) {
    return text.replace(REQUEST_LOG_EVENT_TRANSLATION_TAG_REGEXP, EMPTY_STRING)
  }

  return text
}

/**
 * Resolves the translation key for an event. Nullable fields arrive as one
 * action both ways, so the direction is derived from `data`: empty old value ->
 * "set" key, empty new value -> "removed" key, otherwise the "changed" key.
 * Add-user events with a non-empty `data.comment` get the "with a comment" key.
 */
export function getRequestLogEventTranslationKey(item) {
  const action = item?.action
  const withCommentKey =
    REQUEST_LOG_EVENT_ACTION_WITH_COMMENT_TRANSLATION_KEY[action]

  if (withCommentKey && !isRequestLogEventEmptyValue(item?.data?.comment)) {
    return withCommentKey
  }

  const baseKey = REQUEST_LOG_EVENT_ACTION_TRANSLATION_KEY[action]
  const setKey = REQUEST_LOG_EVENT_ACTION_SET_TRANSLATION_KEY[action]
  const removedKey = REQUEST_LOG_EVENT_ACTION_REMOVED_TRANSLATION_KEY[action]

  if (!setKey && !removedKey) {
    return baseKey
  }

  const data = item?.data || EMPTY_OBJECT
  const hasPrev = !isRequestLogEventEmptyValue(data.old_value)
  const hasCurr = !isRequestLogEventEmptyValue(data.new_value)

  if (!hasPrev && hasCurr) {
    return setKey
  }

  if (hasPrev && !hasCurr) {
    return removedKey
  }

  return baseKey
}

export function getRequestLogEventItemValues({ item, t, timeZone }) {
  const action = item?.action
  const data = item?.data || EMPTY_OBJECT

  if (isRequestLogEventUpdateStatusAction(action)) {
    return {
      curr: getRequestLogEventStatusLabel(data.new_value, t),
      prev: getRequestLogEventStatusLabel(data.old_value, t),
    }
  }

  if (isRequestLogEventUpdateDueDateAction(action)) {
    return {
      curr: formatRequestLogEventDueDate({ timeZone, value: data.new_value }),
      prev: formatRequestLogEventDueDate({ timeZone, value: data.old_value }),
    }
  }

  const relatedValueKey = REQUEST_LOG_EVENT_RELATED_VALUE_KEY[action]

  if (relatedValueKey) {
    return {
      curr: getRequestLogEventValueLabel(data.new_value?.[relatedValueKey]),
      prev: getRequestLogEventValueLabel(data.old_value?.[relatedValueKey]),
    }
  }

  const itemsNameKey = REQUEST_LOG_EVENT_ITEMS_NAME_KEY[action]

  if (itemsNameKey) {
    return {
      comment: data.comment,
      names: (data.items || EMPTY_LIST)
        .map(entry => entry?.[itemsNameKey])
        .filter(Boolean)
        .join(', '),
    }
  }

  if (isRequestLogEventRelatedRequestsAction(action)) {
    const requests = data.items || EMPTY_LIST

    return {
      count: requests.length,
      requests: requests.map(getRequestLogEventRelatedRequestName).join(', '),
    }
  }

  if (isRequestLogEventDocFlowsAction(action)) {
    const docFlows = data.items || EMPTY_LIST

    return {
      count: docFlows.length,
      docFlows: docFlows.map(docFlow => docFlow.name).join(', '),
    }
  }

  return {
    curr: getRequestLogEventValueLabel(data.new_value),
    prev: getRequestLogEventValueLabel(data.old_value),
  }
}

/**
 * Drops events whose `action` the frontend cannot render (no translation key and
 * no backend-text fallback). Passed to the loader as `toPreparedItems` so the
 * stored list matches the rendered list — the infinite-scroll sentinel index is
 * computed against the stored items.
 */
export function toKnownRequestLogEvents(items) {
  return items.filter(
    item => REQUEST_LOG_EVENT_ACTION_TRANSLATION_KEY[item?.action]
  )
}

/**
 * Maps log events to display items for `EntityProfileTabUpdates`. Unknown
 * actions are already filtered out by `toKnownRequestLogEvents` at load time.
 */
export function toTranslatedUpdates({
  items,
  linkContext,
  outboundUuid,
  t,
  timeZone,
}) {
  return toKnownRequestLogEvents(items).map(rawItem => {
    const item = getRequestLogEventNormalizedItem(rawItem)
    const translationKey = getRequestLogEventTranslationKey(item)
    const values = getRequestLogEventItemValues({ item, t, timeZone })

    return {
      ...rawItem,
      content: getRequestLogEventItemContent({
        item,
        linkContext,
        outboundUuid,
        t,
        translationKey,
        values,
      }),
      owner_obj: getEntityProfileTabUpdateOwner(rawItem.initiated_by),
      text: getRequestLogEventItemText({ item, t, translationKey, values }),
      timestamp: rawItem.event_dt,
    }
  })
}
